import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {BIB_URL} from "@utils/env";
import Title from "@components/title";
import AnimatedPage from "@components/animated-page";
import {
    Box,
    Container,
    GridItem,
    SimpleGrid,
    Button,
    Spinner,
    Alert,
    AlertIcon,
    Link,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton, ModalBody, ModalFooter, useDisclosure, Image, HStack, Circle, Icon, Text
} from "@chakra-ui/react";
import {SingleDatepicker} from "@components/date-picker";
import TimePicker from "@components/time-picker";
import ReservationCard from "@components/reservation-card-pc";
import SiteCard from "@components/site-card";
import {useDispatch} from "react-redux";
import {AppDispatch, RootState} from "@src/store";
import {
    clearReservations,
    getFeatures,
    getOpeningTimes,
    getObjectTypes, getReservationPossibilities,
    getSites,
    getObjectImages,
    getSpecialModes, clearFilteredReservations, getReservationSettings, getFloormap,
    getSiteImages, getSiteFreeCount,
} from "@features/reservation/action";
import {setObjectImagesFromCache, setFloorMapFromCache} from "@features/reservation/slice";
import {http} from "@utils/http";
import {useAppSelector} from "@store/hooks";
import moment from "moment/moment";
import {useTranslation} from "react-i18next";
import {formatDate} from "@helpers/helpers";
import useTitle from "@hooks/useTitle";
import {FiAlertCircle, FiExternalLink, FiMap} from "react-icons/fi";

const CACHE_TTL = 60 * 60 * 1000;
const pcSiteAssetCache = new Map<string, { objectImages: any[]; floorMap: any; ts: number }>();

const CreatePCReservation = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {t} = useTranslation();

    const {
        sites,
        siteImages,
        siteFreeCount,
        siteFreeCountLoading,
        filteredReservations,
        isReservationsLoading,
        isLoading,
        openingTimes,
        reservationSettings,
        objectImages,
        floorMap
    } = useAppSelector(
        (state: RootState) => state.reservation
    );

    const {reservationBlockSize, maximumReservationTime} = reservationSettings;

    const [date, setDate] = useState(new Date());
    const [siteID, setSiteID] = useState<null | string>(null);
    const [mergedOpeningTimes, setMergedOpeningTimes] = useState<any[]>([]);
    const [allSiteOpeningTimes, setAllSiteOpeningTimes] = useState<Record<string, any[]>>({});
    const [startTimeArray, setStartTimeArray] = useState<any>();
    const [stopTimeArray, setStopTimeArray] = useState<any>();
    const [selectedStartTime, setSelectedStartTime] = useState('');
    const [selectedEndTime, setSelectedEndTime] = useState('');
    const [showTimeHint, setShowTimeHint] = useState(false);
    const timePickerRef = useRef<HTMLInputElement>(null);
    const {isOpen, onOpen, onClose} = useDisclosure();

    useTitle(t(("documentTitle.reservationManagement")))

    // When a site is selected use its specific opening times, otherwise use the merged union
    const activeOpeningTimes = siteID ? openingTimes : mergedOpeningTimes;

    const refreshFreeCount = (newDate: Date, newStart: string, newEnd: string) => {
        if (newStart && newEnd) {
            dispatch(getSiteFreeCount({date: formatDate(newDate), resStart: newStart, resStop: newEnd}));
        } else {
            dispatch(getSiteFreeCount({date: formatDate(newDate)}));
        }
    };

    const handleStartTimeChange = useCallback((v: string) => {
        setSelectedStartTime(v);
        setSelectedEndTime('');
        setShowTimeHint(false);
        dispatch(clearFilteredReservations());
        dispatch(clearReservations());
    }, [dispatch]);

    const handleEndTimeChange = useCallback((v: string) => {
        setSelectedEndTime(v);
        dispatch(clearFilteredReservations());
        dispatch(clearReservations());
    }, [dispatch]);

    const filterOpenDays = activeOpeningTimes?.filter((f: any) => f.openTimes?.some((o: any) => o?.startTime || o?.stopTime));

    const filteredClosingDays = activeOpeningTimes?.length
        ? activeOpeningTimes.filter((f: any) => !f.openTimes?.some((o: any) => o?.startTime && o?.stopTime))
        : [];
    const closingDays = filteredClosingDays.map((f: any) =>
        new Date(moment(f.date).format('YYYY-MM-DD')).getTime()
    );

    const isSiteClosed = (id: string): boolean => {
        const siteOT = allSiteOpeningTimes[id];
        if (!siteOT) return false;
        const dayEntry = siteOT.find((item: any) => item.date === formatDate(date));
        if (!dayEntry) return true;
        return !dayEntry.openTimes?.some((o: any) => o?.startTime && o?.stopTime);
    };

    const isSiteDisabled = useCallback((id: string): boolean => {
        const closed = isSiteClosed(id);
        if (closed) return true;
        if (siteFreeCountLoading || !siteFreeCount.length) return false;
        const entry = siteFreeCount.find((s: any) => Number(s.SiteID) === Number(id));
        return entry === undefined || Number(entry.FreeCount) === 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteFreeCount, siteFreeCountLoading, allSiteOpeningTimes, date]);

    const handleSiteClick = async (id: string) => {
        if (id === siteID || isSiteDisabled(id)) return;
        if (!selectedStartTime) {
            setShowTimeHint(true);
            timePickerRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
            return;
        }
        setShowTimeHint(false);
        setSiteID(id);
        dispatch(clearFilteredReservations());
        dispatch(clearReservations());

        const cached = pcSiteAssetCache.get(id);
        const cacheValid = cached && Date.now() - cached.ts < CACHE_TTL;

        if (cacheValid) {
            dispatch(setObjectImagesFromCache(cached.objectImages));
            dispatch(setFloorMapFromCache(cached.floorMap));
            await Promise.all([
                dispatch(getOpeningTimes({siteID: id})),
                dispatch(getReservationSettings({siteID: id})),
            ]);
        } else {
            const results = await Promise.all([
                dispatch(getOpeningTimes({siteID: id})),
                dispatch(getReservationSettings({siteID: id})),
                dispatch(getObjectImages({siteID: id})),
                dispatch(getFloormap({siteID: id})),
            ]);
            pcSiteAssetCache.set(id, {
                objectImages: (results[2] as any).payload ?? [],
                floorMap: (results[3] as any).payload ?? {},
                ts: Date.now(),
            });
        }

        if (selectedStartTime && selectedEndTime) {
            await search(id, selectedStartTime, selectedEndTime);
        }
    };

    const search = async (siteIdParam?: string, startParam?: string, stopParam?: string) => {
        const id = siteIdParam ?? siteID;
        const start = startParam ?? selectedStartTime;
        const stop = stopParam ?? selectedEndTime;
        if (id && date && start && stop) {
            await dispatch(getReservationPossibilities({
                siteID: id,
                resStart: formatDate(date) + ' ' + start,
                resStop: formatDate(date) + ' ' + stop
            }));
        }
    };

    const buildTimeArrays = useCallback((blocks: any[], forDate: Date, blockSize: number) => {
        if (!blockSize || !blocks?.length) return;
        const dateStr = formatDate(forDate);
        const isToday = dateStr === formatDate(new Date());
        const ms = 1000 * 60 * blockSize;
        const roundedNow = new Date(Math.floor(new Date().getTime() / ms) * ms);
        const nowStr = isToday
            ? roundedNow.getHours().toString().padStart(2, '0') + ':' + roundedNow.getMinutes().toString().padStart(2, '0')
            : '00:00';

        const pad = (t: string) => t.split(':').slice(0, 2).map((s: string) => s.padStart(2, '0')).join(':');
        const startSet = new Set<string>();
        const stopSet  = new Set<string>();

        for (const block of blocks) {
            if (!block?.startTime || !block?.stopTime) continue;
            const blockStart = pad(block.startTime);
            const blockStop  = pad(block.stopTime);

            const stopM = moment(dateStr + ' ' + blockStop);
            const rem   = stopM.minute() % blockSize;
            const blockEnd = moment(stopM).subtract(rem, 'minutes').format('HH:mm');

            if (isToday && nowStr >= blockEnd) continue;

            const effectiveStart = isToday && nowStr > blockStart ? nowStr : blockStart;
            let cur = moment(dateStr + ' ' + effectiveStart);
            const endM = moment(dateStr + ' ' + blockEnd);

            while (!cur.isAfter(endM)) {
                const t = cur.format('HH:mm');
                if (t < blockEnd) startSet.add(t);
                stopSet.add(t);
                cur = moment(cur).add(blockSize, 'minutes');
            }
            stopSet.add(blockEnd);
        }

        setStartTimeArray(Array.from(startSet).sort());
        setStopTimeArray(Array.from(stopSet).sort());
    }, []);

    const padTime = (t: string) => t.split(':').slice(0, 2).map(s => s.padStart(2, '0')).join(':');

    const effectiveStopTimeArray = useMemo(() => {
        if (!selectedStartTime || !stopTimeArray?.length || !activeOpeningTimes?.length) return stopTimeArray;
        const todayTimes = activeOpeningTimes.find((item: any) => item.date === formatDate(date));
        if (!todayTimes) return stopTimeArray;
        const matchingBlocks = todayTimes.openTimes.filter((b: any) => {
            if (!b?.startTime || !b?.stopTime) return false;
            return selectedStartTime >= padTime(b.startTime) && selectedStartTime < padTime(b.stopTime);
        });
        if (!matchingBlocks.length) return stopTimeArray;
        const maxStop = matchingBlocks.reduce((max: string, b: any) => {
            const s = padTime(b.stopTime);
            return s > max ? s : max;
        }, '00:00');
        return stopTimeArray.filter((t: string) => t <= maxStop);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStartTime, stopTimeArray, activeOpeningTimes, date]);

    // Auto-deselect the selected site when it becomes disabled
    useEffect(() => {
        if (!siteID) return;
        if (isSiteDisabled(siteID)) {
            setSiteID(null);
            dispatch(clearFilteredReservations());
            dispatch(clearReservations());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteFreeCount, siteFreeCountLoading]);

    // Initial load
    // @ts-ignore
    useEffect(() => {
        (async () => {
            const sitesResult = await dispatch(getSites());
            await dispatch(getSiteImages());
            await dispatch(getFeatures());
            await dispatch(getSpecialModes());
            await dispatch(getObjectTypes());
            dispatch(getSiteFreeCount({date: formatDate(new Date())}));

            const loadedSites = (sitesResult.payload as any[]) ?? [];
            if (!loadedSites.length) return;

            // Fetch opening times for all sites in parallel
            const allOT = await Promise.all(
                loadedSites.map((site: any) =>
                    http.get("/site/getOpeningTimes/" + site.ID)
                        .then(r => r.data)
                        .catch(() => [])
                )
            );

            const perSite: Record<string, any[]> = {};
            loadedSites.forEach((site: any, i: number) => {
                perSite[String(site.ID)] = allOT[i] ?? [];
            });
            setAllSiteOpeningTimes(perSite);

            // Merge opening times union across all sites
            const dateMap = new Map<string, Array<{startTime: string; stopTime: string}>>();
            for (const siteOT of allOT) {
                for (const day of (siteOT || [])) {
                    if (!dateMap.has(day.date)) dateMap.set(day.date, []);
                    for (const block of (day.openTimes || [])) {
                        if (block?.startTime && block?.stopTime) {
                            dateMap.get(day.date)!.push(block);
                        }
                    }
                }
            }
            const merged = Array.from(dateMap.entries())
                .map(([d, openTimes]) => ({date: d, openTimes: openTimes.length ? openTimes : [null]}))
                .sort((a, b) => a.date.localeCompare(b.date));
            setMergedOpeningTimes(merged);

            await dispatch(getReservationSettings({siteID: String(loadedSites[0].ID)}));
        })();

        return async () => {
            await dispatch(clearFilteredReservations());
            await dispatch(clearReservations());
        };
    }, [dispatch]);

    // Rebuild time arrays when opening times, block size or date change
    useEffect(() => {
        if (!reservationBlockSize || !activeOpeningTimes?.length) return;
        const dayEntry = activeOpeningTimes.find((item: any) => item.date === formatDate(date));
        if (!dayEntry) return;
        buildTimeArrays(dayEntry.openTimes, date, reservationBlockSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteID, activeOpeningTimes, reservationBlockSize, date, buildTimeArrays]);

    // Refresh free counts when date or time selection changes
    useEffect(() => {
        refreshFreeCount(date, selectedStartTime, selectedEndTime);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, selectedStartTime, selectedEndTime]);

    // Auto-search when end time is set and a site is already selected
    useEffect(() => {
        if (selectedEndTime && siteID && selectedStartTime) {
            dispatch(getReservationPossibilities({
                siteID: siteID,
                resStart: formatDate(date) + ' ' + selectedStartTime,
                resStop: formatDate(date) + ' ' + selectedEndTime,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEndTime]);

    const selectedDateClosed = !!siteID && openingTimes.length > 0 && !isLoading
        && (() => {
            const entry = openingTimes?.find((item: any) => item.date === formatDate(date));
            return !entry || !entry.openTimes?.some((o: any) => o?.startTime || o?.stopTime);
        })();

    return (
        <>
            <Title title={t("createPCReservation.title")}/>
            <AnimatedPage animationType={"FadeIn"}>
                <Container maxW={"container.xl"} p={{base: "22px 16px", md: "45px 32px"}}>

                    {/* Date + time picker */}
                    <Box bg={"custom.gray_100"} padding={"32px"} mb={"32px"} maxW={"2xl"}>
                        <SimpleGrid columns={{base: 1, md: 2}} gap={"20px"}>
                            <GridItem>
                                <SingleDatepicker
                                    name="date-input"
                                    date={date}
                                    onDateChange={(d: Date) => {
                                        setDate(d);
                                        setSelectedStartTime('');
                                        setSelectedEndTime('');
                                        dispatch(clearFilteredReservations());
                                        dispatch(clearReservations());
                                        if (siteID) {
                                            const siteOT = allSiteOpeningTimes[siteID];
                                            const dateStr = formatDate(d);
                                            const dayEntry = siteOT?.find((item: any) => item.date === dateStr);
                                            const willBeClosed = !dayEntry || !dayEntry.openTimes?.some((o: any) => o?.startTime && o?.stopTime);
                                            if (willBeClosed) setSiteID(null);
                                        }
                                    }}
                                    disabled={!filterOpenDays?.length}
                                    maxDate={activeOpeningTimes[activeOpeningTimes?.length - 1]?.date}
                                    closingDays={closingDays}
                                />
                            </GridItem>
                            <GridItem>
                                <TimePicker
                                    ref={timePickerRef}
                                    startTimeArray={startTimeArray}
                                    stopTimeArray={effectiveStopTimeArray}
                                    inputPlaceholder={t('newReservation.search.selectTime.placeholder')}
                                    setSelectedStartTime={handleStartTimeChange}
                                    selectedStartTime={selectedStartTime}
                                    setSelectedEndTime={handleEndTimeChange}
                                    selectedEndTime={selectedEndTime}
                                    disabled={!activeOpeningTimes?.length}
                                    reservationBlockSize={reservationBlockSize}
                                    maximumReservationTime={maximumReservationTime}
                                />
                                {showTimeHint && (
                                    <HStack spacing={1} mt={2}>
                                        <Icon as={FiAlertCircle} color={"orange.500"} boxSize={4}/>
                                        <Text fontSize={"sm"} color={"orange.500"}>
                                            {t("newReservation.search.selectTime.placeholder")}
                                        </Text>
                                    </HStack>
                                )}
                            </GridItem>
                        </SimpleGrid>
                    </Box>

                    {/* Site cards */}
                    <SimpleGrid
                        columns={sites.length <= 2 ? {base: 1, md: 2} : {base: 1, sm: 2, md: 3, lg: 4}}
                        gap={"20px"}
                        mb={"32px"}
                        maxW={sites.length <= 2 ? "2xl" : undefined}
                    >
                        {sites.map(({ID, label}: any) => {
                            const idStr = String(ID);
                            const imageEntry = siteImages.find((s: any) => s.SiteID === ID || s.ID === ID);
                            const countEntry = siteFreeCount.find((s: any) => Number(s.SiteID) === Number(ID));
                            const closed = isSiteClosed(idStr);
                            const disabled = isSiteDisabled(idStr);
                            const freeCount = (!closed && !siteFreeCountLoading && siteFreeCount.length > 0)
                                ? (countEntry !== undefined ? Number(countEntry.FreeCount) : 0)
                                : undefined;
                            return (
                                <SiteCard
                                    key={ID}
                                    id={idStr}
                                    name={label}
                                    siteImage={imageEntry?.SITE_IMAGE ?? imageEntry?.OBJECT_IMAGE ?? null}
                                    freeCount={freeCount}
                                    isSelected={siteID === idStr}
                                    isCountLoading={siteFreeCountLoading}
                                    isClosed={closed}
                                    isDisabled={disabled}
                                    onClick={() => handleSiteClick(idStr)}
                                />
                            );
                        })}
                    </SimpleGrid>

                    {/* Search button — only after site is selected */}
                    {siteID && (
                        <Box bg={"custom.gray_100"} padding={"32px"} mb={"32px"} maxW={"2xl"}>
                            {selectedDateClosed && (
                                <Alert status="warning" mb={"16px"}>
                                    <AlertIcon/>
                                    {t("createPCReservation.siteClosedOnDate")}
                                </Alert>
                            )}
                            <Button
                                variant={'primary'}
                                width={'100%'}
                                onClick={() => search()}
                                isDisabled={!selectedStartTime || !selectedEndTime || selectedDateClosed}
                                isLoading={isReservationsLoading}
                            >
                                {t('newReservation.search.btn')}
                            </Button>
                        </Box>
                    )}

                    {/* Results */}
                    {isReservationsLoading ? <Spinner/> : (
                        <>
                            {filteredReservations.length > 0 && (
                                <>
                                    {(floorMap.url || BIB_URL) && (
                                        <Alert status='info' p={"22px 16px"} mb={"40px"}>
                                            <HStack spacing={"32px"}>
                                                {floorMap.url && (
                                                    <HStack spacing={"12px"}>
                                                        <Circle size='32px' bg='custom.brand_primary' color='white'>
                                                            <Icon as={FiMap}/>
                                                        </Circle>
                                                        <Link textDecoration={"underline"} onClick={onOpen}>
                                                            {t('newReservation.map.title')}
                                                        </Link>
                                                    </HStack>
                                                )}
                                                {BIB_URL && (
                                                    <HStack spacing={"12px"}>
                                                        <Circle size='32px' bg='custom.brand_primary' color='white'>
                                                            <Icon as={FiExternalLink}/>
                                                        </Circle>
                                                        <Link textDecoration={"underline"}
                                                              href={BIB_URL} isExternal>
                                                            {t('newReservation.website.title')}
                                                        </Link>
                                                    </HStack>
                                                )}
                                            </HStack>
                                        </Alert>
                                    )}
                                    {isOpen && (
                                        <Modal isOpen={isOpen} onClose={onClose} isCentered>
                                            <ModalOverlay/>
                                            <ModalContent>
                                                <ModalHeader>{t("newReservation.map.title")}</ModalHeader>
                                                <ModalCloseButton/>
                                                <ModalBody><Image src={floorMap.url}/></ModalBody>
                                                <ModalFooter>
                                                    <Button onClick={onClose}>{t("newReservation.map.close")}</Button>
                                                </ModalFooter>
                                            </ModalContent>
                                        </Modal>
                                    )}
                                </>
                            )}
                            <SimpleGrid columns={{base: 1, lg: 2}} gap={"40px"}>
                                {filteredReservations.map((data: any, index: any) => (
                                    <GridItem key={index}>
                                        <ReservationCard data={data}
                                                         selectedDate={date}
                                                         selectedStartTime={selectedStartTime}
                                                         selectedEndTime={selectedEndTime}
                                                         objectImages={objectImages}
                                                         siteID={siteID ? Number(siteID) : undefined}
                                        />
                                    </GridItem>
                                ))}
                            </SimpleGrid>
                        </>
                    )}
                </Container>
            </AnimatedPage>
        </>
    );
};

export default CreatePCReservation;
